import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LogOut, User, LayoutDashboard, MessageCircle, RefreshCw, Briefcase, Wrench } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import NotificationDropdown from './NotificationDropdown';
import { useUnreadMessages } from '../lib/useUnreadMessages';
import LanguageSwitcher from './LanguageSwitcher';
import { usePricing } from '../hooks/usePricing';

const TopBar = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [logoUrl, setLogoUrl] = useState('/logo-transparent.png');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const { unreadCount: unreadMessages } = useUnreadMessages(profile?.id);
  const { t } = useTranslation();
  const { pricing } = usePricing();

  useEffect(() => {
    // Always use light theme
    document.documentElement.classList.remove('dark');
    try { localStorage.removeItem('theme'); } catch {}
    
    // Fetch custom logo from database
    const fetchLogo = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('setting_value')
          .eq('setting_key', 'app_logo_url')
          .maybeSingle();
        
        if (!error && data && data.setting_value) {
          // Handle both string and object formats
          const url = typeof data.setting_value === 'string' 
            ? data.setting_value 
            : data.setting_value.url;
          if (url) {
            setLogoUrl(url);
            try { localStorage.setItem('app_logo_url', url); } catch {}
          }
        }
      } catch (error) {
        console.log('Using default logo');
      }
    };
    
    fetchLogo();
    
    // Subscribe to logo changes in real-time
    const logoChannel = supabase
      .channel('logo-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_settings',
          filter: 'setting_key=eq.app_logo_url'
        },
        (payload) => {
          console.log('Logo updated:', payload);
          if (payload.new?.setting_value?.url) {
            setLogoUrl(payload.new.setting_value.url);
          }
        }
      )
      .subscribe();
    
    // Check for PWA update availability
    const checkUpdate = () => {
      if (window.pwaUpdateAvailable) {
        setUpdateAvailable(true);
      }
    };
    
    checkUpdate();
    const interval = setInterval(checkUpdate, 10000);
    
    window.addEventListener('swUpdateAvailable', () => setUpdateAvailable(true));
    
    return () => {
      clearInterval(interval);
      supabase.removeChannel(logoChannel);
    };
  }, []);
  
  const handleUpdateClick = () => {
    if (window.triggerPWAUpdate) {
      window.triggerPWAUpdate();
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Logout error:', error);
        toast.error('Failed to logout');
        return;
      }
      // Only call setProfile if it's provided as a prop
      if (typeof setProfile === 'function') {
        setProfile(null);
      }
      navigate('/sign-up');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout exception:', error);
      // Still navigate even if there's an error, as the user session is likely cleared
      if (typeof setProfile === 'function') {
        setProfile(null);
      }
      navigate('/sign-up');
    }
  };

  return (
    <div className="bg-background/80 backdrop-blur-xl border-b border-border/40 sticky top-0 z-40 shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition" 
            onClick={() => navigate('/')}
          >
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="h-10 w-10 object-contain"
              onError={(e) => {
                e.target.src = '/logo-transparent.png';
              }}
            />
            <span className="font-bold text-xl bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent hidden sm:block">
              Bohol Trabaho
            </span>
          </div>

          {/* Jobs / Services segmented nav — desktop/tablet (mobile uses the BottomNav Services
              tab, so keeping it out of the crowded mobile header avoids overflow). Icons + a solid
              active pill so the switch is obvious at a glance (was a faint text pill, easy to miss). */}
          {pricing?.services_enabled && (
            <nav className="hidden sm:flex items-center gap-1 bg-muted rounded-xl p-1" aria-label={t('nav.mainNavigation', 'Main navigation')}>
              {[
                { key: 'jobs', label: t('nav.jobs'), path: '/jobs', Icon: Briefcase },
                { key: 'services', label: t('nav.services'), path: '/services', Icon: Wrench },
              ].map((tab) => {
                const active = tab.key === 'services'
                  ? location.pathname.startsWith('/services')
                  : (location.pathname === '/' || location.pathname.startsWith('/jobs'));
                const { Icon } = tab;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    aria-current={active ? 'page' : undefined}
                    data-testid={`tab-${tab.key}`}
                    onClick={() => {
                      // Reserved for the deferred "remember last-visited tab" root redirect (spec §2 #7 / §6.4).
                      try { localStorage.setItem('services_tab_preference', tab.key); } catch (e) { /* ignore */ }
                      navigate(tab.path);
                    }}
                    className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-lg text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      active
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-background/70'
                    }`}
                  >
                    <Icon size={16} aria-hidden="true" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          )}

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* Update button - shows when update available */}
            {updateAvailable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUpdateClick}
                className="rounded-xl animate-pulse"
                data-testid="topbar-update-btn"
              >
                <RefreshCw size={18} className="md:mr-2 text-primary" />
                <span className="hidden md:inline font-semibold text-primary">Update</span>
                <Badge variant="destructive" className="ml-2 h-2 w-2 p-0 rounded-full" />
              </Button>
            )}
            
            {profile && (
              <>
                {/* PESO staff get a minimal nav (branding + language + logout only);
                    the seeker/employer actions below are hidden for them. */}
                {profile.role !== 'peso_staff' && (
                  <>
                {/* Messages Button */}
                {location.pathname !== '/messages' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/messages')}
                    className="rounded-xl relative"
                    data-testid="messages-button"
                  >
                    <MessageCircle size={18} className="md:mr-2" />
                    <span className="hidden md:inline">Messages</span>
                    {unreadMessages > 0 && (
                      <Badge 
                        variant="destructive" 
                        className="ml-2 px-2 py-0.5 text-xs"
                        data-testid="messages-badge"
                      >
                        {unreadMessages}
                      </Badge>
                    )}
                  </Button>
                )}

                <NotificationDropdown profile={profile} />

                {/* Theme Toggle */}
                {/* Dashboard button - for all authenticated users - hidden on mobile as it's in bottom nav */}
                {profile.role === 'admin' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/admin')}
                    className="rounded-xl hidden md:flex"
                    data-testid="admin-button"
                  >
                    <LayoutDashboard size={20} />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/dashboard')}
                    className="rounded-xl hidden md:flex"
                    data-testid="dashboard-button"
                  >
                    <LayoutDashboard size={20} />
                  </Button>
                )}

                {/* Profile button - for all authenticated users */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/profile')}
                  className="rounded-xl"
                  data-testid="profile-button"
                >
                  <User size={20} />
                </Button>

                  </>
                )}

                {/* PESO staff: back to their dashboard (hidden while already on it) */}
                {profile.role === 'peso_staff' && location.pathname !== '/peso' && location.pathname !== '/dashboard' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/peso')}
                    className="rounded-xl"
                    data-testid="peso-dashboard"
                  >
                    <LayoutDashboard size={18} className="md:mr-2" />
                    <span className="hidden md:inline">Dashboard</span>
                  </Button>
                )}

                {/* PESO staff: browse-only access to job postings (review what employers posted) */}
                {profile.role === 'peso_staff' && location.pathname !== '/jobs' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/jobs')}
                    className="rounded-xl"
                    data-testid="peso-browse-jobs"
                  >
                    <Briefcase size={18} className="md:mr-2" />
                    <span className="hidden md:inline">Browse Jobs</span>
                  </Button>
                )}

                {/* Logout button (always, including PESO staff) */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="rounded-xl text-destructive hover:text-destructive"
                  data-testid="logout-button"
                >
                  <LogOut size={20} />
                </Button>
              </>
            )}

            {!profile && (
              <>
                <Button
                  onClick={async () => {
                    // FIX: Check for stale/orphaned session (authenticated but no profile loaded)
                    // This can happen when:
                    // 1. PWA opens with browser session but profile fetch failed
                    // 2. After signup, profile fetch times out or fails
                    // 3. Session exists but profile was deleted
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      if (session) {
                        // Stale session detected - must clear it before login
                        // Use window.location to force full page reload after signOut
                        // This ensures React state is fully reset (navigate() would cause
                        // redirect loops since isAuthenticated is still true in state)
                        console.log('[TopBar] Clearing stale session and reloading');
                        await supabase.auth.signOut().catch(e => {
                          console.log('[TopBar] SignOut error (non-fatal):', e);
                        });
                        // Full page reload to reset all React state
                        window.location.href = '/sign-up';
                        return;
                      }
                    } catch (e) {
                      console.log('[TopBar] Session check failed:', e);
                    }
                    // No session exists, safe to use React Router
                    navigate('/sign-up');
                  }}
                  size="default"
                  className="rounded-xl px-5 py-2.5 min-h-[44px] min-w-[80px]"
                >
                  Log In
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
